import { APIClient } from "../../../helpers/api_helper";
import i18n from "../../../i18n";
const api = new APIClient();

// Type definitions
interface DateRange {
  start: string;
  end: string;
}

interface FetchSmsLogsParams {
  currentPage?: number;
  rowsPerPage?: number;
  search?: string;
  date?: DateRange;
  // eslint-disable-next-line no-restricted-globals
  status?: string;
}

interface SmsLog {
  id: string;
  recipientsFirstName: string;
  recipientsLastName: string;
  phoneNumber: string;
  workflowTitle: string;
  workflowMessage: string;
  workflowId: string;
  sentAt: string;
  sentResult: string | null;
  pointsCost: string | null;
  body: string;
  subject: string;
  state: string;
  portalId: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

interface SmsLogsMeta {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  isAdminView: boolean;
  filters: any;
}

interface SmsLogsResponse {
  status_code: number;
  message: string;
  data: SmsLog[];
  meta: SmsLogsMeta;
}

interface SmsLogsStatistics {
  ALL: number;
  SUCCESS: number;
  FAILED: number;
  REMAINING_POINTS: number;
}

/**
 * Fetch SMS logs with pagination and filtering
 */
export const fetchSmsLogs = async (
  params: FetchSmsLogsParams
): Promise<any> => {
  const queryParams = new URLSearchParams();

  // Add pagination params
  if (params.currentPage) {
    queryParams.append("page", params.currentPage.toString());
  }
  if (params.rowsPerPage) {
    queryParams.append("take", params.rowsPerPage.toString());
  }

  // Add filter params
  if (params.search && params.search.trim()) {
    queryParams.append("search", params.search.trim());
  }

  if (params.status && params.status !== "all" && params.status !== "") {
    queryParams.append("status", params.status);
  }

  if (params.date?.start) {
    queryParams.append("startDate", params.date.start);
  }

  if (params.date?.end) {
    queryParams.append("endDate", params.date.end);
  }

  // Add default sorting
  queryParams.append("sortBy", "sentAt");
  queryParams.append("sortOrder", "desc");

  try {
    const response = await api.get(`/api/sms-logs?${queryParams.toString()}`);
    return response;
  } catch (error: any) {
    // API helper already handles auth errors and auto-logout
    // Just extract and throw the error message
    const backendMessage =
      error?.response?.data?.message || error?.data?.message || error?.message;

    throw new Error(backendMessage || i18n.t("smsLogs.api.fetchLogsError"));
  }
};

/**
 * Fetch SMS logs statistics
 */
export const fetchSmsLogsStatistics = async (): Promise<SmsLogsStatistics> => {
  try {
    const response = await api.get("/api/dashboard/sms-statistics");

    // Extract the data from the API response
    // The backend returns { status_code, message, data: { ALL, SUCCESS, FAILED, REMAINING_POINTS } }
    const statistics = response.data || response;

    return statistics;
  } catch (error) {
    // API helper already handles auth errors (USER_INACTIVE, TOKEN_EXPIRED, etc.) and auto-logout.
    // Just extract and throw the error message so callers can handle it if needed.
    const backendMessage =
      (error as any)?.message || i18n.t("smsLogs.api.fetchLogsError");

    throw new Error(backendMessage);
  }
};
